import {
  Component,
  Input,
  EventEmitter,
  Output,
  HostBinding,
  ElementRef,
  HostListener,
  ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

@Component({
  selector: 'suka-search',
  template: `
  <div
    class="search"
    [ngClass]="{
      'skeleton': skeleton,
      'toolbar-search': toolbar,
      'toolbar-search--active': toolbar && active
    }"
    role="search"
  >
    <label class="label" [for]="id">{{label}}</label>

    <div *ngIf="skeleton; else enableInput" class="search-input"></div>
    <ng-template #enableInput>
      <input
        #input
        *ngIf="!toolbar || active || value !== ''"
        class="search-input"
        [type]="tableSearch || !toolbar ? 'text' : 'search'"
        role="search"
        [id]="id"
        [value]="value"
        [autocomplete]="autocomplete"
        [placeholder]="placeholder"
        [disabled]="disabled"
        [required]="required"
        (input)="onSearch($event.target.value)"/>
      <button *ngIf="!tableSearch && toolbar" class="toolbar-search__btn" (click)="openSearch()">
        <suka-icon icon="search"></suka-icon>
      </button>
      <suka-icon icon="search"  *ngIf="tableSearch || !toolbar" (click)="openSearch()" class="search-magnifier"></suka-icon>
    </ng-template>

    <button
      *ngIf="tableSearch || !toolbar"
      class="search-close"
      [ngClass]="{
        'search-close--hidden': !value || value.length === 0
      }"
      [title]="clearButtonTitle"
      [attr.aria-label]="clearButtonTitle"
      (click)="clearSearch()"
    >
      <suka-icon icon="x-circle"></suka-icon>
    </button>
  </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: Search,
      multi: true
    }
  ]
})
// tslint:disable-next-line: component-class-suffix
export class Search implements ControlValueAccessor {
  /**
   * Variable used for creating unique ids for search components.
   */
  static searchCount = 0;

  @HostBinding('class.form-item') get containerClass() { return !this.toolbar; }

  /**
   * Set to `true` for a disabled search input.
   */
  @Input() disabled = false;
  /**
   * Set to `true` for a toolbar search component.
   */
  @Input() toolbar = false;
  /**
   * Set to `true` for a loading search component.
   */
  @Input() skeleton = false;
  /**
   * Set to `true` to expand the toolbar search component.
   */
  @Input() active = false;
  /**
   * Specifies whether the search component is used in the table toolbar.
   */
  @Input() tableSearch = false;
  /**
   * Sets the name attribute on the `input` element.
   */
  @Input() name: string;
  /**
   * The unique id for the search component.
   */
  @Input() id = `search-${Search.searchCount}`;
  /**
   * Reflects the required attribute of the `input` element.
   */
  @Input() required: boolean;
  /**
   * Sets the value attribute on the `input` element.
   */
  @Input() value = '';
  /**
   * Sets the autocomplete attribute on the `input` element.
   * For refernece: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#Values
   */
  @Input() autocomplete = 'on';
  /**
   * Sets the text inside the `label` tag.
   */
  @Input() label = 'BÃºsqueda'; // TODO: i18n
  /**
   * Sets the placeholder attribute on the `input` element.
   */
  @Input() placeholder = 'Buscar'; // TODO: i18n
  /**
   * Used to set the `title` attribute of the clear button.
   */
  @Input() clearButtonTitle = 'Limpiar'; // TODO: i18n
  /**
   * Emits an event when value is changed.
   */
  @Output() valueChange = new EventEmitter<string>();
  /**
   * Emits an event when the clear button is clicked.
   */
  @Output() clear = new EventEmitter();

  @ViewChild('input', {static: false}) inputRef: ElementRef;

  /**
   * Creates an instance of `Search`.
   */
  constructor(protected elementRef: ElementRef) {
    Search.searchCount++;
  }

  /**
   * This is the initial value set to the component
   * @param value The input value.
   */
  public writeValue(value: any) {
    this.value = value;
  }

  /**
   * Sets a method in order to propagate changes back to the form.
   */
  public registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  /**
   * Registers a callback to be triggered when the control has been touched.
   * @param fn Callback to be triggered when the search input is touched.
   */
  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  /**
   * Called when search input is blurred. Needed to properly implement `ControlValueAccessor`.
   */
  onTouched: () => any = () => { };

  /**
   * Method set in `registerOnChange` to propagate changes back to the form.
   */
  propagateChange = (_: any) => { };

  /**
   * Called when text is written in the input.
   * @param search The input text.
   */
  onSearch(search: string) {
    this.value = search;
    this.doValueChange();
  }

  /**
   * Called when clear button is clicked.
   */
  clearSearch(): void {
    this.value = '';
    this.clear.emit();
    this.propagateChange(this.value);
  }

  doValueChange() {
    this.valueChange.emit(this.value);
    this.propagateChange(this.value);
  }

  openSearch() {
    this.active = true;
    setTimeout(() => this.inputRef.nativeElement.focus());
  }

  @HostListener('keydown', ['$event'])
  keyDown(event: KeyboardEvent) {
    if (this.toolbar) {
      if (event.key === 'Escape') {
        this.active = false;
      } else if (event.key === 'Enter') {
        this.openSearch();
      }
    }
  }

  @HostListener('focusout', ['$event'])
  focusOut(event) {
    if (this.toolbar &&
      this.inputRef.nativeElement.value === '' &&
      event.relatedTarget === null) {
      this.active = false;
    }
  }
}
